/**
 * Shared design tokens — MUST stay identical in customer + admin.
 * Max 3 brand colors; status colors are semantic only.
 * Radius stays subtle/light across the product UI.
 */
export const DESIGN_TOKENS = {
  radius: { sm: 4, md: 6, lg: 6, xl: 8 },
  space: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    pageX: 24,
    pageY: 20,
    card: 16,
    section: 20,
  },
  typography: {
    fontFamily: '"Poppins", "Outfit", system-ui, sans-serif',
    h1: 28,
    h2: 22,
    h3: 18,
    h4: 16,
    body: 14,
    label: 13,
    button: 14,
    caption: 12,
    lineHeight: 1.55,
  },
  shadow: {
    sm: "0 1px 2px rgba(15,23,42,0.03)",
    md: "0 2px 8px rgba(15,23,42,0.05)",
    lg: "0 4px 16px rgba(15,23,42,0.06)",
  },
  shadowDark: {
    sm: "0 1px 2px rgba(0,0,0,0.25)",
    md: "0 2px 8px rgba(0,0,0,0.3)",
    lg: "0 4px 14px rgba(0,0,0,0.35)",
  },
  border: { width: 1 },
  layout: {
    sidebarExpanded: 260,
    sidebarCollapsed: 72,
    navbarHeight: 60,
    drawerZ: 1400,
    overlayZ: 1390,
  },
  defaults: {
    primaryColor: "#02026E",
    secondaryColor: "#0F172A",
    accentColor: "#0F172A",
    success: "#059669",
    warning: "#D97706",
    error: "#DC2626",
  },
};

export function applyBrandCssVars(brand = {}, mode = "light") {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const brandPrimary = brand.primaryColor || DESIGN_TOKENS.defaults.primaryColor;
  const brandSecondary = brand.secondaryColor || DESIGN_TOKENS.defaults.secondaryColor;
  const accent = brand.accentColor || DESIGN_TOKENS.defaults.accentColor;
  const isDark = mode === "dark";

  // Light mode uses primary color, Dark mode uses secondary color
  const primary = isDark ? brandSecondary : brandPrimary;
  const secondary = isDark ? brandPrimary : brandSecondary;

  const set = (k, v) => root.style.setProperty(k, v);

  set("--color-primary", primary);
  set("--color-secondary", secondary);
  set("--color-accent", accent);
  set("--primary-color", primary);
  set("--secondary-color", secondary);
  set("--accent-color", accent);
  set("--brand-primary", brandPrimary);
  set("--brand-secondary", brandSecondary);
  set("--success-color", DESIGN_TOKENS.defaults.success);
  set("--pending-color", DESIGN_TOKENS.defaults.warning);
  set("--failed-color", DESIGN_TOKENS.defaults.error);
  set("--error-color", DESIGN_TOKENS.defaults.error);
  set("--warning-color", DESIGN_TOKENS.defaults.warning);

  set("--radius-sm", `${DESIGN_TOKENS.radius.sm}px`);
  set("--radius-md", `${DESIGN_TOKENS.radius.md}px`);
  set("--radius-lg", `${DESIGN_TOKENS.radius.lg}px`);
  set("--radius-xl", `${DESIGN_TOKENS.radius.xl}px`);

  set("--space-xs", `${DESIGN_TOKENS.space.xs}px`);
  set("--space-sm", `${DESIGN_TOKENS.space.sm}px`);
  set("--space-md", `${DESIGN_TOKENS.space.md}px`);
  set("--space-lg", `${DESIGN_TOKENS.space.lg}px`);
  set("--space-xl", `${DESIGN_TOKENS.space.xl}px`);
  set("--page-px", `${DESIGN_TOKENS.space.pageX}px`);
  set("--page-py", `${DESIGN_TOKENS.space.pageY}px`);
  set("--card-padding", `${DESIGN_TOKENS.space.card}px`);

  set("--font-family", DESIGN_TOKENS.typography.fontFamily);
  set("--text-body", `${DESIGN_TOKENS.typography.body}px`);
  set("--text-label", `${DESIGN_TOKENS.typography.label}px`);
  set("--text-caption", `${DESIGN_TOKENS.typography.caption}px`);
  set("--text-button", `${DESIGN_TOKENS.typography.button}px`);
  set("--line-height", String(DESIGN_TOKENS.typography.lineHeight));

  set("--shadow-sm", isDark ? DESIGN_TOKENS.shadowDark.sm : DESIGN_TOKENS.shadow.sm);
  set("--shadow-md", isDark ? DESIGN_TOKENS.shadowDark.md : DESIGN_TOKENS.shadow.md);
  set("--shadow-lg", isDark ? DESIGN_TOKENS.shadowDark.lg : DESIGN_TOKENS.shadow.lg);

  set("--border-width", `${DESIGN_TOKENS.border.width}px`);
  set("--border-color", isDark ? "#2A3648" : "#E2E8F0");

  set("--bg-app", isDark ? "#0B1220" : "#F4F7FB");
  set("--bg-color", isDark ? "#0B1220" : "#F4F7FB");
  set("--surface-color", isDark ? "#151E2E" : "#FFFFFF");
  // Text colors strictly change based on theme toggle (isDark), NOT brand primary/secondary
  set("--text-dark", isDark ? "#F8FAFC" : "#0F172A");
  set("--text-muted", isDark ? "#94A3B8" : "#64748B");
  set("--text-light", isDark ? "#94A3B8" : "#64748B");
  set("--text-primary", isDark ? "#F8FAFC" : "#0F172A");
  set("--text-secondary", isDark ? "#94A3B8" : "#64748B");

  set("--sidebar-width", `${DESIGN_TOKENS.layout.sidebarExpanded}px`);
  set("--sidebar-collapsed", `${DESIGN_TOKENS.layout.sidebarCollapsed}px`);
}

export function resolveMediaUrl(baseUrl, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(baseUrl || "").replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}
